import React, { useState, useEffect } from 'react';
import rbacApi from '../../api/rbacApi';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Grid,
  Divider,
  Pagination,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Search,
  FilterList,
  Download,
  Refresh,
  Security,
  Person,
  Folder,
  Group,
  Lock,
  Info,
  Warning,
  Error,
  History,
  AccessTime,
  LocationOn,
  Computer
} from '@mui/icons-material';
import { dsVar, ink } from '../../theme/tokens';

// Mock audit log data with detailed information
const mockAuditLogs = [
  {
    id: '1',
    action: 'User Added to Project',
    user: 'John Doe',
    userEmail: 'john.doe@company.com',
    target: 'Project Alpha',
    details: 'Added user "Jane Smith" as Project Editor with cross-group access permissions',
    timestamp: '2024-01-15 14:30:25',
    type: 'user_management',
    severity: 'info',
    ipAddress: '192.168.1.100',
    userAgent: 'Chrome/120.0.0.0',
    location: 'New York, US',
    changes: {
      before: 'No access',
      after: 'Project Editor with cross-group permissions',
      affectedUser: 'Jane Smith (jane.smith@company.com)'
    },
    metadata: {
      projectId: 'proj_001',
      userGroup: 'Engineering Team',
      permissions: ['read', 'write', 'cross_group_access']
    }
  },
  {
    id: '2',
    action: 'Group Admin Removed',
    user: 'Admin User',
    userEmail: 'admin@company.com',
    target: 'Marketing Team',
    details: 'Removed "Mike Johnson" as group admin and converted to regular member',
    timestamp: '2024-01-15 13:45:12',
    type: 'permission_change',
    severity: 'warning',
    ipAddress: '192.168.1.101',
    userAgent: 'Firefox/121.0.0.0',
    location: 'San Francisco, US',
    changes: {
      before: 'Group Admin',
      after: 'Regular Member',
      affectedUser: 'Mike Johnson (mike.johnson@company.com)'
    },
    metadata: {
      userGroupId: 'group_002',
      reason: 'Role restructuring',
      newAdmin: 'Sarah Wilson'
    }
  },
  {
    id: '3',
    action: 'Project Created',
    user: 'Jane Smith',
    userEmail: 'jane.smith@company.com',
    target: 'API Integration v2',
    details: 'Created new project "API Integration v2" in Marketing Team with 3 initial members',
    timestamp: '2024-01-15 12:20:45',
    type: 'project_management',
    severity: 'info',
    ipAddress: '192.168.1.102',
    userAgent: 'Safari/17.2.0.0',
    location: 'London, UK',
    changes: {
      before: 'Project did not exist',
      after: 'Project created with owner permissions',
      affectedUser: 'Jane Smith (project owner)'
    },
    metadata: {
      projectId: 'proj_003',
      userGroup: 'Marketing Team',
      initialMembers: ['Jane Smith', 'David Brown', 'Lisa Chen'],
      projectType: 'Development'
    }
  },
  {
    id: '4',
    action: 'Failed Login Attempt',
    user: 'Unknown',
    userEmail: 'test@company.com',
    target: 'System Authentication',
    details: 'Failed login attempt with incorrect password - account temporarily locked',
    timestamp: '2024-01-15 11:15:30',
    type: 'security',
    severity: 'error',
    ipAddress: '203.0.113.45',
    userAgent: 'PostmanRuntime/7.32.3',
    location: 'Unknown',
    changes: {
      before: 'Account active',
      after: 'Account temporarily locked',
      affectedUser: 'test@company.com'
    },
    metadata: {
      failedAttempts: 5,
      lockoutDuration: '15 minutes',
      securityLevel: 'High'
    }
  },
  {
    id: '5',
    action: 'Cross-Group Access Granted',
    user: 'Admin User',
    userEmail: 'admin@company.com',
    target: 'Project Beta',
    details: 'Granted cross-group access to Sarah Wilson from Sales Team to Engineering Team project',
    timestamp: '2024-01-15 10:30:18',
    type: 'permission_change',
    severity: 'info',
    ipAddress: '192.168.1.100',
    userAgent: 'Chrome/120.0.0.0',
    location: 'New York, US',
    changes: {
      before: 'No cross-group access',
      after: 'Cross-group access granted',
      affectedUser: 'Sarah Wilson (sarah.wilson@company.com)'
    },
    metadata: {
      sourceGroup: 'Sales Team',
      targetGroup: 'Engineering Team',
      projectId: 'proj_002',
      permissions: ['read', 'comment']
    }
  }
];

const ActivityLogs: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(1);



  // Load audit logs from backend
  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page,
        limit: rowsPerPage,
        search: searchTerm || undefined,
        type: filterType !== 'all' ? filterType : undefined,
      };

      const response = await rbacApi.getAuditLogs(params);
      
      if (response && Array.isArray(response)) {
        // Real data from backend (already mapped)
        setAuditLogs(response);
        setTotalPages(Math.ceil(response.length / rowsPerPage));
      } else if (response && response.activityLogs) {
        // Raw backend response - map it dynamically
        const mappedLogs = response.activityLogs.map((log: any) => {
          // Dynamic field mapping - handle different possible field names
          const logId = log._id || log.id || log.logId || log.log_id;
          const logUserId = log.userId || log.user_id || log.user || log.userEmail || log.user_email;
          const logActionType = log.actionType || log.action_type || log.action || log.type;
          const logEntityType = log.entityType || log.entity_type || log.targetType || log.target_type;
          const logEntityId = log.entityId || log.entity_id || log.targetId || log.target_id;
          const logDescription = log.description || log.desc || log.message || log.summary;
          const logDetails = log.details || log.metadata || log.data || {};
          const logSuccess = log.success !== undefined ? log.success : true;
          const logTimestamp = log.timestamp || log.createdAt || log.created_at || log.date;
          
          // Map action type to readable format
          let mappedAction = 'Unknown Action';
          if (logActionType) {
            const action = logActionType.toString().toUpperCase();
            switch (action) {
              case 'CREATE_USER_GROUP':
                mappedAction = 'Created User Group';
                break;
              case 'UPDATE_USER_GROUP':
                mappedAction = 'Updated User Group';
                break;
              case 'DELETE_USER_GROUP':
                mappedAction = 'Deleted User Group';
                break;
              case 'ADD_GROUP_MEMBER':
                mappedAction = 'Added Group Member';
                break;
              case 'REMOVE_GROUP_MEMBER':
                mappedAction = 'Removed Group Member';
                break;
              case 'CREATE_USER':
                mappedAction = 'Created User';
                break;
              case 'UPDATE_USER':
                mappedAction = 'Updated User';
                break;
              case 'DELETE_USER':
                mappedAction = 'Deleted User';
                break;
              case 'CREATE_PROJECT':
                mappedAction = 'Created Project';
                break;
              case 'UPDATE_PROJECT':
                mappedAction = 'Updated Project';
                break;
              case 'DELETE_PROJECT':
                mappedAction = 'Deleted Project';
                break;
              case 'LOGIN':
                mappedAction = 'User Login';
                break;
              case 'LOGOUT':
                mappedAction = 'User Logout';
                break;
              case 'PERMISSION_CHANGE':
                mappedAction = 'Changed Permissions';
                break;
              default:
                mappedAction = logActionType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l: string) => l.toUpperCase());
            }
          }
          
          // Map severity based on success and action type
          let mappedSeverity = 'info';
          if (!logSuccess) {
            mappedSeverity = 'error';
          } else if (logActionType) {
            const action = logActionType.toString().toUpperCase();
            if (['DELETE_USER_GROUP', 'DELETE_USER', 'DELETE_PROJECT', 'REMOVE_GROUP_MEMBER'].includes(action)) {
              mappedSeverity = 'warning';
            }
          }
          
          // Extract user name from userId (assuming it's an email)
          const userName = logUserId && logUserId.includes('@') ? logUserId.split('@')[0] : (logUserId || 'Unknown User');
          
          return {
            id: logId,
            action: mappedAction,
            details: logDescription || 'No details available',
            user: userName,
            userEmail: logUserId || 'no-email@example.com',
            target: logEntityType || 'Unknown',
            type: logActionType?.toLowerCase().replace(/_/g, ' ') || 'unknown',
            severity: mappedSeverity,
            timestamp: logTimestamp,
            success: logSuccess,
            entityId: logEntityId,
            entityType: logEntityType,
            metadata: logDetails
          };
        });
        
        setAuditLogs(mappedLogs);
        setTotalPages(Math.ceil(mappedLogs.length / rowsPerPage));
      } else {
        // Fallback to mock data if backend is not available
        setAuditLogs(mockAuditLogs);
        setTotalPages(Math.ceil(mockAuditLogs.length / rowsPerPage));
      }
    } catch (err) {
      console.error('Error loading audit logs:', err);
      setError('Failed to load audit logs. Using mock data.');
      // Fallback to mock data
      setAuditLogs(mockAuditLogs);
      setTotalPages(Math.ceil(mockAuditLogs.length / rowsPerPage));
    } finally {
      setLoading(false);
    }
  };

  // Load audit logs on component mount and when filters change
  useEffect(() => {
    loadAuditLogs();
  }, [page, searchTerm, filterType]);

  // Handle export logs
  const handleExportLogs = async () => {
    try {
      const params = {
        search: searchTerm || undefined,
        type: filterType !== 'all' ? filterType : undefined,
      };
      
      const response = await rbacApi.exportAuditLogs(params);
      
      // Create and download file
      const blob = new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error exporting logs:', err);
      alert('Failed to export audit logs');
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    loadAuditLogs();
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'user_management':
        return <Person />;
      case 'permission_change':
        return <Security />;
      case 'project_management':
        return <Folder />;
      case 'security':
        return <Lock />;
      case 'data_export':
        return <Download />;
      default:
        return <Info />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'user_management':
        return 'primary';
      case 'permission_change':
        return 'warning';
      case 'project_management':
        return 'secondary';
      case 'security':
        return 'error';
      case 'data_export':
        return 'info';
      default:
        return 'default';
    }
  };

  const filteredLogs = auditLogs.filter(log => {
    const matchesSearch = log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         log.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         log.target.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || log.type === filterType;
    return matchesSearch && matchesType;
  });

  return (
    <Box>
      {/* Activity Logs Table */}
      <Card sx={{ 
        bgcolor: dsVar('surface'), 
        borderRadius: '16px', 
        border: `1px solid ${dsVar('line')}`,
        boxShadow: 'none',
        overflow: 'hidden'
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
            <Typography variant="h6" sx={{ color: dsVar('fg'), fontWeight: 700 }}>
              Recent Activities
            </Typography>
            <Box display="flex" gap={2}>
              <TextField
                size="small"
                placeholder="Search activities..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: dsVar('page'),
                    color: dsVar('fg'),
                    borderRadius: '8px',
                    '& fieldset': { borderColor: dsVar('line') },
                    '&:hover fieldset': { borderColor: dsVar('accent-fg') },
                    '&.Mui-focused fieldset': { borderColor: dsVar('accent-fg') },
                  },
                  '& .MuiInputBase-input::placeholder': { color: dsVar('fg-2'), opacity: 1 }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: dsVar('fg-2') }} />
                    </InputAdornment>
                  ),
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                sx={{
                  color: dsVar('fg-2'),
                  borderColor: dsVar('line'),
                  borderRadius: '8px',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: dsVar('accent-fg'),
                    color: dsVar('accent-fg'),
                    bgcolor: 'rgba(255, 222, 89, 0.05)'
                  }
                }}
              >
                Filter
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none', border: `1px solid ${dsVar('line')}`, borderRadius: '12px', overflow: 'hidden' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: dsVar('page') }}>
                  <TableCell sx={{ color: dsVar('fg-2'), fontWeight: 600, borderBottom: `1px solid ${dsVar('line')}` }}>Action</TableCell>
                  <TableCell sx={{ color: dsVar('fg-2'), fontWeight: 600, borderBottom: `1px solid ${dsVar('line')}` }}>User</TableCell>
                  <TableCell sx={{ color: dsVar('fg-2'), fontWeight: 600, borderBottom: `1px solid ${dsVar('line')}` }}>Target</TableCell>
                  <TableCell sx={{ color: dsVar('fg-2'), fontWeight: 600, borderBottom: `1px solid ${dsVar('line')}` }}>Type</TableCell>
                  <TableCell sx={{ color: dsVar('fg-2'), fontWeight: 600, borderBottom: `1px solid ${dsVar('line')}` }}>Severity</TableCell>
                  <TableCell sx={{ color: dsVar('fg-2'), fontWeight: 600, borderBottom: `1px solid ${dsVar('line')}` }}>Timestamp</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8, border: 'none' }}>
                      <CircularProgress sx={{ color: dsVar('accent-fg') }} size={24} />
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, border: 'none' }}>
                      <Alert severity="warning" sx={{ bgcolor: 'rgba(245, 158, 11, 0.1)', color: ink('#f59e0b'), border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                        <Typography variant="body2">
                          {error}
                        </Typography>
                      </Alert>
                    </TableCell>
                  </TableRow>
                ) : filteredLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8, color: dsVar('fg-2'), border: 'none' }}>
                      No audit logs found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLogs.map((log) => (
                    <TableRow key={log.id} sx={{ '&:hover': { bgcolor: 'rgba(255, 222, 89, 0.02)' }, '& td': { borderBottom: `1px solid ${dsVar('line')}` } }}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ color: dsVar('fg'), fontWeight: 600 }}>
                            {log.action}
                          </Typography>
                          <Typography variant="caption" sx={{ color: dsVar('fg-2') }}>
                            {log.details}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ 
                            width: 24, 
                            height: 24, 
                            bgcolor: dsVar('line'), 
                            color: dsVar('accent-fg'), 
                            fontSize: '0.7rem', 
                            fontWeight: 700 
                          }}>
                            {log.user.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ color: dsVar('fg'), fontSize: '0.8125rem' }}>
                              {log.user}
                            </Typography>
                            <Typography variant="caption" sx={{ color: dsVar('fg-2'), fontSize: '0.6875rem' }}>
                              {log.userEmail}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: dsVar('fg') }}>
                          {log.target}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getTypeIcon(log.type)}
                          label={log.type.replace('_', ' ').toUpperCase()}
                          size="small"
                          variant="outlined"
                          sx={{
                            color: dsVar('fg-2'),
                            borderColor: dsVar('line'),
                            borderRadius: '4px',
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: 'inherit', fontSize: '1rem' }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.severity.toUpperCase()}
                          size="small"
                          sx={{
                            bgcolor: log.severity === 'error' ? 'rgba(239, 68, 68, 0.1)' : 
                                     log.severity === 'warning' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                            color: log.severity === 'error' ? ink('#ef4444') : 
                                   log.severity === 'warning' ? '#f59e0b' : '#3b82f6',
                            fontWeight: 700,
                            borderRadius: '4px',
                            fontSize: '0.65rem'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ color: dsVar('fg') }}>
                            {new Date(log.timestamp).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" sx={{ color: dsVar('fg-2') }}>
                            {new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box display="flex" justifyContent="center" mt={4}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(event, value) => setPage(value)}
              sx={{
                '& .MuiPaginationItem-root': {
                  color: dsVar('fg-2'),
                  '&:hover': {
                    bgcolor: 'rgba(255, 222, 89, 0.1)',
                    color: dsVar('accent-fg')
                  },
                  '&.Mui-selected': {
                    bgcolor: 'rgba(255, 222, 89, 0.2)',
                    color: dsVar('accent-fg'),
                    fontWeight: 700,
                    '&:hover': {
                      bgcolor: 'rgba(255, 222, 89, 0.3)'
                    }
                  }
                }
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ActivityLogs; 